import {
  Close as CloseIcon,
  IosShare,
  Language,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  Link,
  Snackbar,
  Typography,
} from '@mui/material'
import copy from 'copy-to-clipboard'
import React, { useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { AppAPIClient } from '../../lib/index'
import { formatLocationString } from '../../lib/utils/formatLocationString'
import { isMobile } from '../../lib/utils/isMobile'
import { normalizeUrl } from '../../lib/utils/normalizeUrl'
import type { SearchPerkResult } from '../../types'
import PerkBadges from '../shared/PerkBadges'
import { BadgeLayout } from '../shared/types/badge.types'

interface PerkModalProps {
  perk: SearchPerkResult | null
  open: boolean
  onClose: () => void
  canRedeemPerks: boolean
}

const PerkModal: React.FC<PerkModalProps> = ({ perk, open, onClose, canRedeemPerks }) => {
  const [isRedeemed, setIsRedeemed] = React.useState(false)
  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const auth = useAuth()
  const [api] = useState(new AppAPIClient(auth))

  // Reset redemption state and snackbar when modal closes
  React.useEffect(() => {
    if (!open) {
      setIsRedeemed(false)
      setSnackbarOpen(false)
      setSnackbarMessage('')
    }
  }, [open])

  // Track perk view when modal opens
  React.useEffect(() => {
    if (open && perk) {
      api.trackAnalytics('VIEWED_PERK', { perkId: perk.id })
    }
  }, [open, perk, api])

  if (!perk) return null

  const formatValue = (value: number, currency = 'USD') => {
    if (!value) return null
    return `$${value.toLocaleString()} ${currency}`
  }

  const getCompanyInitial = (companyName: string) => {
    return companyName?.charAt(0)?.toUpperCase() || '?'
  }

  const handleVisitWebsite = () => {
    if (perk.companyUrl) {
      window.open(normalizeUrl(perk.companyUrl), '_blank')
    }
  }

  const handleShare = async () => {
    const url = window.location.href

    try {
      if (!navigator?.canShare) throw new Error()
      if (isMobile()) {
        // on mobile
        await navigator.share({
          title: `${perk.companyName} Perk`,
          text: perk.perkShortDesc,
          url,
        })
        api.trackAnalytics('SHARED_PERK', { perkId: perk.id, method: 'mobile' })
      } else {
        copy(url)
        api.trackAnalytics('SHARED_PERK', {
          perkId: perk.id,
          method: 'copy_link',
        })
      }
      setSnackbarMessage('Link copied to clipboard')
      setSnackbarOpen(true)
    } catch {
      setSnackbarMessage('Unable to copy link')
      setSnackbarOpen(true)
    }
  }

  const handleRedeem = async (id: string) => {
    // only accelerator entrepreneurs with a company id can redeem perks
    try {
      await api.v1_perks.redeemPerk(id)
      setIsRedeemed(true)
      setSnackbarMessage(
        'Perk redeemed! Follow the instructions to claim your offer.',
      )
      setSnackbarOpen(true)
    } catch (err) {
      console.error('Error redeeming perk:', err)
      setSnackbarMessage('Error redeeming perk.')
      setSnackbarOpen(true)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {/* Close Button */}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            zIndex: 1,
            bgcolor: 'rgba(255,255,255,0.9)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header Section */}
        <Box sx={{ p: 4, pb: 3 }}>
          {/* Company Logo and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar
              src={perk.logoUrl || undefined}
              alt={perk.companyName}
              sx={{
                width: 80,
                height: 80,
                bgcolor: perk.logoUrl ? 'transparent' : '#000',
                fontSize: '32px',
                fontWeight: 'bold',
                mr: 3,
                color: perk.logoUrl ? 'inherit' : '#fff',
                border: '1px solid #e0e0e0',
              }}
            >
              {!perk.logoUrl && getCompanyInitial(perk.companyName)}
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, mb: 1, fontSize: '28px' }}
              >
                {perk.companyName}
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: 1,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'black',
                }}
              >
                {perk.perkShortDesc}
              </Typography>
              {perk.value && (
                <Typography
                  variant="h5"
                  sx={{
                    mt: 1,
                    color: 'rgba(0 , 0 , 0 , 0.6 )',
                    fontSize: '16px',
                  }}
                >
                  ({formatValue(perk.value, perk.valueCurrency)} Value)
                </Typography>
              )}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
            <Button
              variant="text"
              startIcon={<Language />}
              onClick={handleVisitWebsite}
              disabled={!perk.companyUrl}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1976d2',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Visit Website
            </Button>
            <Button
              variant="text"
              startIcon={<IosShare />}
              onClick={handleShare}
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1976d2',
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              Share
            </Button>
          </Box>
        </Box>

        <Divider />

        {/* Content */}
        <Box sx={{ p: 4, borderRadius: '0 0 8px 8px' }}>
          {/* Blurred Redemption Section */}
          <Box sx={{ position: 'relative', mb: 4 }}>
            {/* Blur Overlay and Redeem Button (only when not redeemed) */}
            {!isRedeemed && (
              <Box
                sx={{
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)', // Safari support
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgb(240, 248, 240)',
                  borderRadius: 1,
                  minHeight: '120px',
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => handleRedeem(perk.id)}
                  disabled={!canRedeemPerks}
                  sx={{
                    py: 2,
                    px: 6,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '18px',
                    bgcolor: 'rgba(52, 117, 178, 1)',
                    borderRadius: 2,
                  }}
                >
                  Redeem Perk
                </Button>
                {!canRedeemPerks && (
                  <Typography variant="body2" color="text.secondary">
                    You do not have permission to redeem perks. If you
                    believe this is an error, please contact{' '}
                    <a href="mailto:help@techstars.com">help@techstars.com</a>.
                  </Typography>
                )}
              </Box>
            )}

            {/* Revealed content after redemption */}
            {isRedeemed && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgb(240, 248, 240)',
                  borderRadius: 1,
                  minHeight: '120px',
                }}
              >
                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                  {perk.redemptionHtml ? (
                    <span
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: <HTML pre-sanitized>
                      dangerouslySetInnerHTML={{ __html: perk.redemptionHtml }}
                    />
                  ) : (
                    <>
                      To redeem this perk, visit the {perk.companyName} website
                      and sign up using your Techstars email address.
                      <br />
                      <br />
                      Make sure to mention that you're a Techstars founder to
                      receive the special offer.
                    </>
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Location - Always Visible */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <LocationIcon
              sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }}
            />
            <Typography variant="body2" color="text.secondary">
              Available in {formatLocationString(perk.locations)}
            </Typography>
          </Box>

          {/* About Section - Always Visible */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              fontSize: '16px',
              color: 'rgb(117, 117, 117)',
            }}
          >
            About
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.6, fontSize: '16px' }}
          >
            {perk.companyDescHtml ? (
              <span
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <HTML pre-sanitized>
                dangerouslySetInnerHTML={{ __html: perk.companyDescHtml }}
              />
            ) : (
              <span>No description provided</span>
            )}
          </Typography>

          {/* Details Section - Always Visible */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              fontSize: '16px',
              color: 'rgb(117, 117, 117)',
            }}
          >
            Details
          </Typography>
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.6, fontSize: '16px', mb: 4 }}
          >
            {perk.perkLongDescHtml ? (
              <span
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <HTML pre-sanitized>
                dangerouslySetInnerHTML={{ __html: perk.perkLongDescHtml }}
              />
            ) : (
              <span>No description provided</span>
            )}
          </Typography>

          {/* Requirements */}
          {perk.requirementsHtml && (
            <>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  fontSize: '16px',
                  color: 'rgb(117, 117, 117)',
                }}
              >
                Requirements
              </Typography>

              <span
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <HTML pre-sanitized>
                dangerouslySetInnerHTML={{ __html: perk.requirementsHtml }}
              />
            </>
          )}

          {/* Expiration Date */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: '16px',
              color: 'rgb(117, 117, 117)',
            }}
          >
            Expiration Date
          </Typography>
          <Typography
            variant="body1"
            sx={{ mb: 4, lineHeight: 1.6, fontSize: '16px' }}
          >
            {perk.expiresAt
              ? new Date(perk.expiresAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : 'No expiration date'}
          </Typography>

          {/* Categories */}
          <PerkBadges
            isTechstarsCompany={perk.isTechstarsCompany}
            isGlobalNetworkPartner={perk.isGlobalNetworkPartner}
            categories={perk.categories}
            layout={BadgeLayout.HORIZONTAL}
          />

          {/* Footer */}
          <Typography sx={{ mt: 2, fontSize: '12px' }}>
            Perks are intended for use by Techstars companies. Do not sell or
            redeem perks for use by other entities. Questions? Email Founder
            Resources at{' '}
            <Link href="mailto:perks@techstars.com" rel="noopener noreferrer">
              perks@techstars.com
            </Link>
          </Typography>
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Dialog>
  )
}

export default PerkModal
